import Phaser from "phaser";
import { detectPlatform } from "@/platform/detectPlatform";
import { platformLabel, type Platform } from "@/platform/Platform";
import { GameConfig } from "@/config/GameConfig";
import { createButton } from "@/ui/Button";
import { DailyChallengeClient, type DailyLeaderboardRow } from "@/daily/DailyChallengeClient";
import { getChallengeDay } from "@/daily/nyTime";
import { DAILY_INCOMPLETE_LABEL } from "@/daily/statusLabels";
import { desiredTitleSize, computeHeaderLayout, rowContentBottom } from "@/ui/leaderboardLayout";
import { attachOrientationGate, type OrientationGateHandle } from "@/scenes/OrientationGate";

export interface DailyLeaderboardSceneData {
  challengeId?: string;
}

type LoadState = "loading" | "ok" | "empty" | "offline" | "error";

const PAGE_SIZE = 25;
const ROW_HEIGHT = 32;
const LIST_WIDTH_MAX = 460;

/**
 * Separate from the campaign `LeaderboardScene` on purpose - Daily
 * Challenge ranking (completed-above-DNF, score, active time, steps,
 * submission order) has nothing to do with the campaign's stars/level
 * ranking, and the two must never be mixed (see the PR description).
 * Loads incrementally (`LOAD MORE`) rather than a single unbounded
 * fetch, so it stays safe at any participant count.
 */
export class DailyLeaderboardScene extends Phaser.Scene {
  private challengeId = "";
  private requestToken = 0;
  private loadState: LoadState = "loading";
  private errorMessage = "";
  private rows: DailyLeaderboardRow[] = [];
  private myRank: number | null = null;
  private hasMore = true;

  private mainContainer!: Phaser.GameObjects.Container;
  private listContainer!: Phaser.GameObjects.Container;
  /** PR31 follow-up fix: see `OrientationGate.ts`'s own doc. No `requiresExplicitResume` - this Scene has no run/state to protect, so rotating back to portrait restores interaction immediately. */
  private orientationGate!: OrientationGateHandle;
  /** PR34: the viewer's Mobile/Desktop board - never a toggle, never legacy `unknown` rows. */
  private platform: Platform = "desktop";

  constructor() {
    super("DailyLeaderboardScene");
  }

  create(data: DailyLeaderboardSceneData): void {
    this.challengeId = data.challengeId ?? getChallengeDay(Date.now()).challengeId;
    // PR34: decided once per visit - the board and "YOU" rank below both read it.
    this.platform = detectPlatform();
    this.requestToken = 0;
    this.loadState = "loading";
    this.rows = [];
    this.myRank = null;
    this.hasMore = true;

    this.cameras.main.setBackgroundColor(GameConfig.colors.background);
    this.mainContainer = this.add.container(0, 0);
    this.buildLayout();
    this.scale.on("resize", this.onResize, this);
    this.events.once("shutdown", () => this.teardown());
    this.events.once("destroy", () => this.teardown());
    this.orientationGate = attachOrientationGate(this);
    this.orientationGate.check();

    void this.refresh();
  }

  private teardown(): void {
    this.scale.off("resize", this.onResize, this);
  }

  private onResize(): void {
    this.buildLayout();
  }

  private goBack(): void {
    this.scene.start("DailyChallengeScene");
  }

  private async refresh(): Promise<void> {
    const token = ++this.requestToken;
    this.loadState = "loading";
    this.renderList();

    const [entriesResult, rankResult] = await Promise.all([
      DailyChallengeClient.getLeaderboard(this.challengeId, this.platform, PAGE_SIZE, 0),
      DailyChallengeClient.getMyRank(this.challengeId, this.platform),
    ]);
    if (token !== this.requestToken) return;

    if (entriesResult.state === "ok" && entriesResult.rows) {
      this.rows = entriesResult.rows;
      this.hasMore = entriesResult.rows.length === PAGE_SIZE;
      this.loadState = entriesResult.rows.length === 0 ? "empty" : "ok";
    } else {
      this.loadState = entriesResult.state === "offline" ? "offline" : "error";
      this.errorMessage = entriesResult.message ?? "";
    }
    this.myRank = rankResult.state === "ok" && rankResult.rank?.hasResult ? rankResult.rank.rank : null;

    this.renderList();
  }

  private async loadMore(): Promise<void> {
    const token = this.requestToken;
    const result = await DailyChallengeClient.getLeaderboard(this.challengeId, this.platform, PAGE_SIZE, this.rows.length);
    if (token !== this.requestToken || result.state !== "ok" || !result.rows) return;
    this.rows = [...this.rows, ...result.rows];
    this.hasMore = result.rows.length === PAGE_SIZE;
    this.renderList();
  }

  private buildLayout(): void {
    this.mainContainer.removeAll(true);
    const { width, height } = this.scale;
    const centerX = width / 2;

    // Mobile beta fix (PR29, 3rd round): BACK and the title no longer fight
    // over a horizontal safe zone on mobile at all - below
    // `LB_MOBILE_HEADER_BREAKPOINT` (`computeHeaderLayout()`), BACK gets
    // its own row and the title gets its own row below it, centered, with
    // the FULL content width available. Human QA found the previous
    // shrink/wrap-into-a-safe-zone approach (round 2; still what desktop
    // uses, where title/BACK do still share a row) produced
    // readable-but-ugly results on mobile even once it stopped overlapping
    // BACK - this gives the title enough room that it never needs to wrap
    // in practice. `wordWrap` stays only as a defensive backstop.
    const initialLayout = computeHeaderLayout(width, height);
    const titleSize = desiredTitleSize(width);
    const title = this.add
      .text(centerX, initialLayout.titleY, "DAILY LEADERBOARD", {
        fontFamily: "Arial, sans-serif",
        fontSize: `${titleSize}px`,
        fontStyle: "bold",
        color: "#f5f7fa",
        align: "center",
        wordWrap: { width: initialLayout.titleMaxWidth, useAdvancedWrap: true },
      })
      .setOrigin(0.5);
    this.mainContainer.add(title);

    // Second pass: the title's REAL measured height (a no-op whenever it
    // rendered on one line - the common case at every supported width).
    const layout = computeHeaderLayout(width, height, title.height);

    const backButton = createButton(this, layout.backX, layout.backY, "BACK", () => this.goBack(), {
      width: layout.backWidth,
      height: layout.backHeight,
      fontSize: "15px",
      fillColor: 0x1c2030,
      hoverColor: 0x262c40,
      textColor: "#f5f7fa",
    });
    this.mainContainer.add(backButton);

    // Flows from the title's own REAL measured height (never assumed) - a
    // no-op whenever it rendered on one line, but keeps the date clear of
    // a wrapped title in the rare case wordWrap's backstop kicked in.
    const dateText = this.add
      .text(centerX, title.y + title.height / 2 + 6, `${this.challengeId} · ${platformLabel(this.platform)}`, {
        fontFamily: "Arial, sans-serif",
        fontSize: "13px",
        color: "#8b93a7",
      })
      .setOrigin(0.5, 0);
    this.mainContainer.add(dateText);

    this.listContainer = this.add.container(centerX, dateText.y + dateText.height + 20);
    this.mainContainer.add(this.listContainer);

    this.renderList();
  }

  private renderList(): void {
    if (!this.listContainer) return;
    this.listContainer.removeAll(true);
    const width = Math.min(LIST_WIDTH_MAX, this.scale.width - 32);

    switch (this.loadState) {
      case "loading":
        this.renderStatusMessage(width, "LOADING…");
        return;
      case "offline":
        this.renderStatusMessage(width, "LEADERBOARD UNAVAILABLE — CHECK YOUR CONNECTION", true);
        return;
      case "error":
        this.renderStatusMessage(width, this.errorMessage || "Something went wrong.", true);
        return;
      case "empty":
        this.renderStatusMessage(width, "BE THE FIRST TO SUBMIT A RESULT TODAY");
        return;
      case "ok":
        this.renderRows(width);
        return;
    }
  }

  private renderStatusMessage(width: number, message: string, showRetry = false): void {
    const text = this.add
      .text(0, 20, message, {
        fontFamily: "Arial, sans-serif",
        fontSize: "15px",
        fontStyle: "bold",
        color: "#8b93a7",
        align: "center",
        wordWrap: { width: width - 20 },
      })
      .setOrigin(0.5, 0);
    this.listContainer.add(text);
    if (showRetry) {
      const retry = createButton(this, 0, text.y + text.height + 20, "RETRY", () => void this.refresh(), { width: 200, height: 46 });
      this.listContainer.add(retry);
    }
  }

  private renderRows(width: number): void {
    let y = 0;
    for (const row of this.rows) {
      y = this.renderRow(width, y, row);
    }
    y += 8;

    if (this.myRank !== null && !this.rows.some((r) => r.rank === this.myRank)) {
      const you = this.add
        .text(-width / 2, y, "YOU", { fontFamily: "Arial, sans-serif", fontSize: "11px", color: "#4a5168" })
        .setOrigin(0, 0);
      this.listContainer.add(you);
      y += 16;
      const meText = this.add
        .text(-width / 2, y, `#${this.myRank}`, { fontFamily: "Arial, sans-serif", fontSize: "14px", fontStyle: "bold", color: "#5b8cff" })
        .setOrigin(0, 0);
      this.listContainer.add(meText);
      y += ROW_HEIGHT;
    }

    if (this.hasMore) {
      const loadMore = createButton(this, 0, y + 16, "LOAD MORE", () => void this.loadMore(), {
        width: 200,
        height: 44,
        fillColor: 0x1c2030,
        hoverColor: 0x262c40,
        textColor: "#f5f7fa",
      });
      this.listContainer.add(loadMore);
    }
  }

  /**
   * Mobile beta fix (round 2): a max-16-char display name (`NAME_MAX_LENGTH`
   * in `NameEntryModal.ts`) routinely measures WIDER than the name column's
   * available `wordWrap` width on a narrow phone (confirmed via real canvas
   * text metrics), so Phaser wraps it to a second line - and this scene's
   * `ROW_HEIGHT` (32px) is even tighter than a wrapped 2-line 14px name's
   * own rendered height, so the NEXT row (fixed `y += ROW_HEIGHT` stepping)
   * used to start ON TOP of that second line whenever it actually wrapped.
   * Fixed by measuring `nameText`'s own real (post-wrap) `.height` and
   * flowing the next row from that instead of a fixed assumption - a no-op
   * whenever the name fits on one line (the previous, common case). Returns
   * the Y the NEXT row must start at, replacing the caller's own fixed
   * `+= ROW_HEIGHT` stepping.
   */
  private renderRow(width: number, y: number, row: DailyLeaderboardRow): number {
    const isMe = row.rank === this.myRank;

    const rankText = this.add
      .text(-width / 2, y, `#${row.rank}`, { fontFamily: "Arial, sans-serif", fontSize: "14px", fontStyle: "bold", color: "#8b93a7" })
      .setOrigin(0, 0);

    const nameText = this.add
      .text(-width / 2 + 46, y, row.displayName, {
        fontFamily: "Arial, sans-serif",
        fontSize: "14px",
        color: "#f5f7fa",
        wordWrap: { width: width - 190 },
      })
      .setOrigin(0, 0);

    // Daily Points is the headline number for a V2 (Generator V2) day -
    // `row.dailyPoints` is null only for an already-stored V1 (legacy) day,
    // which falls back to showing the raw score exactly as it always did.
    const primaryValue = row.dailyPoints !== null ? `${row.dailyPoints} pts` : `${row.score}`;
    const scoreText = this.add
      .text(width / 2 - (row.completed ? 0 : 56), y, primaryValue, {
        fontFamily: "Arial, sans-serif",
        fontSize: "14px",
        fontStyle: "bold",
        color: "#f5f7fa",
      })
      .setOrigin(1, 0);

    let dnfBadge: Phaser.GameObjects.Text | null = null;
    if (!row.completed) {
      dnfBadge = this.add
        .text(width / 2, y, DAILY_INCOMPLETE_LABEL, {
          fontFamily: "Arial, sans-serif",
          fontSize: "10px",
          fontStyle: "bold",
          color: "#ff5f6d",
        })
        .setOrigin(1, 0);
    }

    // The row's real bottom edge - the tallest of every element actually
    // placed in it - never smaller than ROW_HEIGHT, so a normal
    // single-line row keeps its original, already-comfortable spacing. See
    // `rowContentBottom()`'s own doc for the wrapped-name overlap bug this
    // replaces (this scene's tight 32px ROW_HEIGHT made it the worse case).
    const contentBottom = rowContentBottom(y, ROW_HEIGHT, [rankText.y + rankText.height, nameText.y + nameText.height, scoreText.y + scoreText.height, dnfBadge ? dnfBadge.y + dnfBadge.height : 0]);

    if (isMe) {
      // Sized/positioned from the same real content bottom, and added
      // FIRST (index 0) so it renders behind the text this row already
      // created above, even though it's created after them.
      const bgHeight = contentBottom - y - 2;
      const bg = this.add.rectangle(0, y + bgHeight / 2 - 2, width, bgHeight, GameConfig.colors.accent, 0.12);
      this.listContainer.addAt(bg, 0);
    }

    this.listContainer.add(rankText);
    this.listContainer.add(nameText);
    this.listContainer.add(scoreText);
    if (dnfBadge) this.listContainer.add(dnfBadge);

    return contentBottom;
  }
}
